import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Unstable_Grid2";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import { formatDate, parseExcelDate, normalizeInventoryStatus } from "utils";
import { filterInputSx, filterSelectSx, filterFormControlSx } from "utils/ui";
import { createSvgIcon } from "@mui/material/utils";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { setLoadingPanel } from "../../redux/features/loading";
import { useDispatch, useSelector } from "react-redux";
import CircularIndeterminateModal from "../../utils/Loading";
import {
  getInventoryFilter,
  addInventory,
  editInventory,
} from "apis/inventoryApi";
import { BrandsApi } from "apis/BrandsApi";
import { ProductsApi } from "apis/ProductsApi";
import { UsersApi } from "apis/UsersApi";

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "SELL_IN", label: "Sell In" },
  { value: "SELL_OUT", label: "Sell Out" },
  { value: "ACTIVATED", label: "Activated" },
];

const PlusIcon = createSvgIcon(
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.5v15m7.5-7.5h-15"
    />
  </svg>,
  "Plus"
);

const initialFormData = {
  status: "SELL_IN",
  imei1: "",
  imei2: "",
  sn: "",
  image: "",
  brandId: "",
  productId: "",
  userId: "",
  sellInDate: new Date().toISOString().slice(0, 10),
  sellOutDate: "",
  activationDate: "",
};

export default function Inventory() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);

  const [inventory, setInventory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [filterPayload, setFilterPayload] = useState({
    brandId: "",
    productId: "",
    status: "",
    imei: "",
    size: 25,
  });

  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]); 
  const [formProducts, setFormProducts] = useState([]); 

  const [users, setUsers] = useState([]);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const [productSearchInput, setProductSearchInput] = useState("");
  const [formProductSearchInput, setFormProductSearchInput] = useState("");
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [formProductSearchLoading, setFormProductSearchLoading] = useState(false);
  const productSearchTimeout = useRef(null);
  const formProductSearchTimeout = useRef(null);

  const [imeiInput, setImeiInput] = useState("");
  const imeiDebounceTimeout = useRef(null);

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusDate, setStatusDate] = useState("");

  const fileInputRef = useRef(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const fetchInventory = useCallback(async () => {
    try {
      dispatch(setLoadingPanel(true));
      const response = await getInventoryFilter({
        brandId: filterPayload.brandId || null,
        productId: filterPayload.productId || null,
        status: filterPayload.status || null,
        imei: filterPayload.imei || null,
        page: currentPage - 1,
        size: filterPayload.size,
      });
      setInventory(response.data.data || []);
      setTotalPages(response.data.pages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch inventory");
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      dispatch(setLoadingPanel(false));
    }
  }, [filterPayload, currentPage, dispatch, navigate]);

  const getProductName = (product) => {
    const primary = product?.primaryName || "";
    const secondary = product?.secondaryName || "";
    return secondary ? `${primary} ${secondary}` : primary;
  };

  useEffect(() => {
    BrandsApi.GetBrandsListByFilter({ page: 0, size: 99 })
      .then((res) => setBrands(res.data.data || []))
      .catch(() => toast.error("Failed to load brands"));
  }, []);

  const searchUsers = async (mobile) => {
    if (mobile.length === 10) {
      try {
        const res = await UsersApi.GetUsersListByFilter({
          page: 0,
          size: 20,
          mobile: mobile,
        });
        const foundUsers = res.data.data || [];
        setUsers(foundUsers);
        if (foundUsers.length === 1) {
          setSelectedUser(foundUsers[0]);
          handleFormChange("userId", foundUsers[0].id);
        } else if (foundUsers.length === 0) {
          setSelectedUser(null);
          handleFormChange("userId", "");
        }
      } catch {
        setUsers([]);
        setSelectedUser(null);
        handleFormChange("userId", "");
      }
    } else {
      setUsers([]);
      setSelectedUser(null);
      handleFormChange("userId", "");
    }
  };

  const searchFilterProducts = (keyword) => {
    if (productSearchTimeout.current) {
      clearTimeout(productSearchTimeout.current);
    }

    if (!keyword || keyword.trim().length < 2) {
      setProducts([]);
      return;
    }

    productSearchTimeout.current = setTimeout(async () => {
      if (!filterPayload.brandId) {
        setProducts([]);
        return;
      }
      setProductSearchLoading(true);
      try {
        const res = await ProductsApi.SearchProducts({
          page: 0,
          size: 50,
          keyword: keyword.trim(),
          brandId: filterPayload.brandId,
          isActive: true,
        });
        setProducts(res.data.data || []);
      } catch {
        setProducts([]);
      } finally {
        setProductSearchLoading(false);
      }
    }, 300);
  };

  const searchFormProducts = (keyword) => {
    if (formProductSearchTimeout.current) {
      clearTimeout(formProductSearchTimeout.current);
    }

    if (!keyword || keyword.trim().length < 2) {
      setFormProducts([]);
      return;
    }

    formProductSearchTimeout.current = setTimeout(async () => {
      if (!formData.brandId) {
        setFormProducts([]);
        return;
      }
      setFormProductSearchLoading(true);
      try {
        const res = await ProductsApi.SearchProducts({
          page: 0,
          size: 50,
          keyword: keyword.trim(),
          brandId: formData.brandId,
          isActive: true,
        });
        setFormProducts(res.data.data || []);
      } catch {
        setFormProducts([]);
      } finally {
        setFormProductSearchLoading(false);
      }
    }, 300);
  };

  const prevFilterBrandId = useRef(filterPayload.brandId);
  useEffect(() => {
    if (prevFilterBrandId.current !== filterPayload.brandId) {
      setProducts([]);
      setProductSearchInput("");
      prevFilterBrandId.current = filterPayload.brandId;
    }
  }, [filterPayload.brandId]);

  useEffect(() => {
    fetchInventory();
  }, [filterPayload, currentPage]);

  const handleFilterChange = (name, value) => {
    setFilterPayload((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "brandId" ? { productId: "" } : {}),
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    if (imeiDebounceTimeout.current) {
      clearTimeout(imeiDebounceTimeout.current);
    }
    setImeiInput("");
    setProductSearchInput("");
    setProducts([]);
    setFilterPayload({
      brandId: "",
      productId: "",
      status: "",
      imei: "",
      size: 25,
    });
    setCurrentPage(1);
  };

  const handleClickOpen = () => {
    setFormData(initialFormData);
    setUserSearchInput("");
    setSelectedUser(null);
    setUsers([]);
    setIsEdit(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setUserSearchInput("");
    setSelectedUser(null);
    setUsers([]);
    setFormProducts([]);
    setFormProductSearchInput("");
    setIsEdit(false);
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "brandId" ? { productId: "" } : {}),
    }));
    if (name === "brandId") {
      setFormProducts([]);
      setFormProductSearchInput("");
    }
  };

  const handleSave = async () => {
    try {
      dispatch(setLoadingPanel(true));
      const payload = { ...formData };

      if (!payload.sellInDate) delete payload.sellInDate;
      if (!payload.sellOutDate) delete payload.sellOutDate;
      if (!payload.activationDate) delete payload.activationDate;

      if (isEdit) {
        await editInventory(payload);
        toast.success("Inventory updated successfully");
      } else {
        await addInventory(payload);
        toast.success("Inventory added successfully");
      }
      handleClose();
      fetchInventory();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed");
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      dispatch(setLoadingPanel(false));
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      status: item.status || "SELL_IN",
      imei1: item.imei1 || "",
      imei2: item.imei2 || "",
      sn: item.sn || "",
      image: item.image || "",
      brandId: item.brand?.id || "",
      productId: item.product?.id || "",
      userId: item.user?.id || "",
      sellInDate: item.sellInDate
        ? new Date(item.sellInDate).toISOString().slice(0, 10)
        : "",
      sellOutDate: item.sellOutDate
        ? new Date(item.sellOutDate).toISOString().slice(0, 10)
        : "",
      activationDate: item.activationDate
        ? new Date(item.activationDate).toISOString().slice(0, 10)
        : "",
    });
    if (item.product) {
      setFormProducts([item.product]);
      setFormProductSearchInput(getProductName(item.product));
    } else {
      setFormProducts([]);
      setFormProductSearchInput("");
    }
    if (item.user) {
      setSelectedUser(item.user);
      setUserSearchInput(item.user.mobile || "");
      setUsers([item.user]);
    } else {
      setSelectedUser(null);
      setUserSearchInput("");
      setUsers([]);
    }
    setIsEdit(true);
    setOpen(true);
  };

  const handleOpenStatusDialog = (item) => {
    setSelectedInventory(item);
    setNewStatus(item.status);

    let initialDate = "";
    if (item.status === "SELL_IN" && item.sellInDate) {
      initialDate = new Date(item.sellInDate).toISOString().slice(0, 10);
    } else if (item.status === "SELL_OUT" && item.sellOutDate) {
      initialDate = new Date(item.sellOutDate).toISOString().slice(0, 10);
    } else if (item.status === "ACTIVATED" && item.activationDate) {
      initialDate = new Date(item.activationDate).toISOString().slice(0, 10);
    } else {
      initialDate = new Date().toISOString().slice(0, 10);
    }

    setStatusDate(initialDate);
    setStatusDialogOpen(true);
  };

  const downloadTemplate = () => {
    const headers = [
      "Brand Name",
      "Product Secondary Name",
      "IMEI 1",
      "IMEI 2",
      "Serial Number",
      "Status (SELL_IN / SELL_OUT / ACTIVATED)",
      "User Mobile",
      "Sell In Date",
      "Sell Out Date",
      "Activation Date",
      "Image URL"
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory Template");
    
    ws['!cols'] = [
      { wch: 20 },
      { wch: 25 },
      { wch: 18 },
      { wch: 18 },
      { wch: 15 },
      { wch: 40 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 }
    ];

    XLSX.writeFile(wb, "Inventory_Upload_Template.xlsx");
    toast.success("Template downloaded successfully");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      dispatch(setLoadingPanel(true));
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            toast.error("Excel file is empty");
            dispatch(setLoadingPanel(false));
            return;
          }

          const results = {
            total: jsonData.length,
            success: 0,
            failed: 0,
            errors: []
          };

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const rowNum = i + 2; 

            try {
              const brand = brands.find(
                (b) => b.name.toLowerCase() === row["Brand Name"]?.toString().toLowerCase().trim()
              );
              
              if (!brand) {
                throw new Error(`Brand "${row["Brand Name"]}" not found`);
              }

              const productRes = await ProductsApi.SearchProducts({
                page: 0,
                size: 50,
                keyword: row["Product Secondary Name"]?.toString().trim(),
                brandId: brand.id,
                isActive: true,
              });

              const product = productRes.data.data?.find(
                (p) => p.secondaryName?.toLowerCase() === row["Product Secondary Name"]?.toString().toLowerCase().trim()
              );

              if (!product) {
                throw new Error(`Product "${row["Product Secondary Name"]}" not found for brand "${brand.name}"`);
              }

              let userId = "";
              if (row["User Mobile"]) {
                const mobile = row["User Mobile"].toString().trim();
                if (mobile.length === 10) {
                  const userRes = await UsersApi.GetUsersListByFilter({
                    page: 0,
                    size: 1,
                    mobile: mobile,
                  });
                  if (userRes.data.data?.length > 0) {
                    userId = userRes.data.data[0].id;
                  }
                }
              }

              const statusInput = normalizeInventoryStatus(row["Status"]);

              const payload = {
                status: statusInput,
                imei1: row["IMEI 1"]?.toString().trim() || "",
                imei2: row["IMEI 2"]?.toString().trim() || "",
                sn: row["Serial Number"]?.toString().trim() || "",
                image: row["Image URL"]?.toString().trim() || "",
                brandId: brand.id,
                productId: product.id,
                userId: userId,
                sellInDate: parseExcelDate(row["Sell In Date"]),
                sellOutDate: parseExcelDate(row["Sell Out Date"]),
                activationDate: parseExcelDate(row["Activation Date"]),
              };

              if (!payload.sellInDate) delete payload.sellInDate;
              if (!payload.sellOutDate) delete payload.sellOutDate;
              if (!payload.activationDate) delete payload.activationDate;
              if (!payload.userId) delete payload.userId;

              await addInventory(payload);
              results.success++;
            } catch (err) {
              results.failed++;
              results.errors.push({
                row: rowNum,
                imei: row["IMEI 1"] || "N/A",
                error: err.message || err?.response?.data?.message || "Unknown error"
              });
            }
          }

          setUploadResults(results);
          setUploadDialogOpen(true);
          fetchInventory();
          
          if (results.success > 0) {
            toast.success(`Successfully uploaded ${results.success} of ${results.total} items`);
          }
          if (results.failed > 0) {
            toast.warning(`${results.failed} items failed to upload`);
          }
        } catch (err) {
          toast.error("Failed to process Excel file: " + err.message);
        } finally {
          dispatch(setLoadingPanel(false));
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error("Failed to read file");
      dispatch(setLoadingPanel(false));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedInventory || !newStatus) return;
    try {
      dispatch(setLoadingPanel(true));

      const payload = {
        id: selectedInventory.id,
        status: newStatus,
        imei1: selectedInventory.imei1 || "",
        imei2: selectedInventory.imei2 || "",
        sn: selectedInventory.sn || "",
        image: selectedInventory.image || "",
        brandId: selectedInventory.brand?.id || "",
        productId: selectedInventory.product?.id || "",
        userId: selectedInventory.user?.id || "",
        sellInDate: selectedInventory.sellInDate
          ? new Date(selectedInventory.sellInDate).toISOString().slice(0, 10)
          : "",
        sellOutDate: selectedInventory.sellOutDate
          ? new Date(selectedInventory.sellOutDate).toISOString().slice(0, 10)
          : "",
        activationDate: selectedInventory.activationDate
          ? new Date(selectedInventory.activationDate).toISOString().slice(0, 10)
          : "",
      };

      if (newStatus === "SELL_IN" && statusDate) {
        payload.sellInDate = statusDate;
      } else if (newStatus === "SELL_OUT" && statusDate) {
        payload.sellOutDate = statusDate;
      } else if (newStatus === "ACTIVATED" && statusDate) {
        payload.activationDate = statusDate;
      }

      if (!payload.sellInDate) delete payload.sellInDate;
      if (!payload.sellOutDate) delete payload.sellOutDate;
      if (!payload.activationDate) delete payload.activationDate;

      await editInventory(payload);
      toast.success("Status updated successfully");
      setStatusDialogOpen(false);
      setSelectedInventory(null);
      setStatusDate("");
      fetchInventory();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      dispatch(setLoadingPanel(false));
    }
  };

  return (
    <>
      <CircularIndeterminateModal open={isLoading} />

      {/* Filters Section */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
        <div className="block w-full overflow-x-auto p-4 xl:p-8">
          <Grid container spacing={2}>
            {/* First Row: Brand and Product */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={filterFormControlSx}>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={filterPayload.brandId}
                  label="Brand"
                  onChange={(e) => handleFilterChange("brandId", e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={!filterPayload.brandId}
                options={products}
                getOptionLabel={(option) => getProductName(option)}
                value={products.find((p) => p.id === filterPayload.productId) || null}
                onChange={(_, newValue) => {
                  handleFilterChange("productId", newValue?.id || "");
                }}
                inputValue={productSearchInput}
                onInputChange={(_, newInputValue, reason) => {
                  setProductSearchInput(newInputValue);
                  if (reason === "input") {
                    searchFilterProducts(newInputValue);
                  }
                }}
                loading={productSearchLoading}
                noOptionsText={
                  !filterPayload.brandId
                    ? "Select Brand First"
                    : productSearchInput.length < 2
                    ? "Type to search products..."
                    : "No products found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    placeholder={filterPayload.brandId ? "Type to search..." : "Select Brand First"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {productSearchLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* Second Row: Status, IMEI, and Buttons */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={filterFormControlSx}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterPayload.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  sx={filterSelectSx}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                sx={filterInputSx}
                label="Search IMEI"
                value={imeiInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setImeiInput(value);

                  if (imeiDebounceTimeout.current) {
                    clearTimeout(imeiDebounceTimeout.current);
                  }

                  imeiDebounceTimeout.current = setTimeout(() => {
                    setFilterPayload((prev) => ({
                      ...prev,
                      imei: value,
                    }));
                    setCurrentPage(1);
                  }, 500);
                }}
              />
            </Grid>

            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={clearFilters}
                sx={{ height: "40px" }}
              >
                Reset
              </Button>
            </Grid>

            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="info"
                onClick={downloadTemplate}
                sx={{ height: "40px" }}
                startIcon={<DownloadIcon />}
              >
                Template
              </Button>
            </Grid>

            <Grid item xs={6} md={2}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
              <Button
                fullWidth
                variant="contained"
                color="success"
                onClick={() => fileInputRef.current?.click()}
                sx={{ height: "40px" }}
                startIcon={<UploadFileIcon />}
              >
                Upload
              </Button>
            </Grid>

            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleClickOpen}
                sx={{ height: "40px" }}
                startIcon={<PlusIcon />}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Table Section */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
        <div className="block w-full overflow-x-auto py-3 px-5 lg:p-8">
          <div className="flex justify-between items-center p-2 mb-2">
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ ...filterFormControlSx, minWidth: 80 }}>
                <InputLabel>Size</InputLabel>
                <Select
                  value={filterPayload.size}
                  label="Size"
                  onChange={(e) => handleFilterChange("size", e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Pagination
                count={totalPages}
                page={currentPage}
                color="primary"
                onChange={handlePageChange}
              />
              <span className="text-sm font-medium">
                Total: {totalElements}
              </span>
            </Stack>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr className="divide-x divide-gray-200">
                <TableHeader heading="#" />
                <TableHeader heading="Brand" />
                <TableHeader heading="Product" />
                <TableHeader heading="IMEI 1" />
                <TableHeader heading="IMEI 2" />
                <TableHeader heading="Serial No." />
                <TableHeader heading="Status" />
                <TableHeader heading="User" />
                <TableHeader heading="Sell In" />
                <TableHeader heading="Sell Out" />
                <TableHeader heading="Activation" />
                <TableHeader heading="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {inventory.map((item, i) => (
                <tr
                  key={item.id}
                  className="divide-x divide-gray-200 hover:bg-slate-100"
                >
                  <TableElement
                    value={filterPayload.size * (currentPage - 1) + i + 1}
                  />
                  <TableElement value={item.brand?.name || "-"} />
                  <TableElement value={getProductName(item.product) || "-"} />
                  <TableElement value={item.imei1 || "-"} />
                  <TableElement value={item.imei2 || "-"} />
                  <TableElement value={item.sn || "-"} />
                  <TableElement value={item.status || "-"} />
                  <TableElement value={item.user?.name || item.user?.mobile || "-"} />
                  <TableElement value={item.sellInDate ? formatDate(item.sellInDate) : "-"} />
                  <TableElement value={item.sellOutDate ? formatDate(item.sellOutDate) : "-"} />
                  <TableElement value={item.activationDate ? formatDate(item.activationDate) : "-"} />
                  <TableElement
                    value={
                      <div className="flex gap-2">
                        <span
                          className="cursor-pointer text-sky-500 hover:text-sky-950 font-bold"
                          onClick={() => handleEdit(item)}
                        >
                          EDIT
                        </span>
                        <span
                          className="cursor-pointer text-orange-500 hover:text-orange-700 font-bold"
                          onClick={() => handleOpenStatusDialog(item)}
                        >
                          STATUS
                        </span>
                      </div>
                    }
                    type="element"
                  />
                </tr>
              ))}
              {inventory.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center py-4 text-gray-500">
                    No inventory found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} component="div">
              {isEdit ? "Edit Inventory" : "Add Inventory"}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Brand *</InputLabel>
                <Select
                  value={formData.brandId}
                  label="Brand *"
                  onChange={(e) => handleFormChange("brandId", e.target.value)}
                >
                  <MenuItem value="">Select Brand</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={!formData.brandId}
                options={formProducts}
                getOptionLabel={(option) => getProductName(option)}
                value={formProducts.find((p) => p.id === formData.productId) || null}
                onChange={(_, newValue) => {
                  handleFormChange("productId", newValue?.id || "");
                }}
                inputValue={formProductSearchInput}
                onInputChange={(_, newInputValue, reason) => {
                  setFormProductSearchInput(newInputValue);
                  if (reason === "input") {
                    searchFormProducts(newInputValue);
                  }
                }}
                loading={formProductSearchLoading}
                noOptionsText={
                  !formData.brandId
                    ? "Select Brand First"
                    : formProductSearchInput.length < 2
                    ? "Type to search products..."
                    : "No products found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product *"
                    placeholder={formData.brandId ? "Type to search..." : "Select Brand First"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {formProductSearchLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="User Mobile"
                value={userSearchInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setUserSearchInput(value);
                  searchUsers(value);
                }}
                placeholder="Enter 10 digit mobile"
                helperText={
                  userSearchInput.length === 10
                    ? users.length > 0
                      ? `Found: ${selectedUser?.name || users[0]?.name || "N/A"} (${selectedUser?.mobile || users[0]?.mobile})`
                      : "No user found"
                    : `Enter 10 digit mobile (${userSearchInput.length}/10)`
                }
                error={userSearchInput.length === 10 && users.length === 0}
                color={userSearchInput.length === 10 && users.length > 0 ? "success" : undefined}
              />
              {users.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>Select User</InputLabel>
                  <Select
                    value={formData.userId}
                    label="Select User"
                    onChange={(e) => {
                      const user = users.find((u) => u.id === e.target.value);
                      setSelectedUser(user);
                      handleFormChange("userId", e.target.value);
                    }}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.mobile} - {user.name || "N/A"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.status}
                  label="Status *"
                  onChange={(e) => handleFormChange("status", e.target.value)}
                >
                  <MenuItem value="SELL_IN">Sell In</MenuItem>
                  <MenuItem value="SELL_OUT">Sell Out</MenuItem>
                  <MenuItem value="ACTIVATED">Activated</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="IMEI 1"
                value={formData.imei1}
                onChange={(e) => handleFormChange("imei1", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="IMEI 2"
                value={formData.imei2}
                onChange={(e) => handleFormChange("imei2", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Serial Number"
                value={formData.sn}
                onChange={(e) => handleFormChange("sn", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.image}
                onChange={(e) => handleFormChange("image", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Sell In Date"
                value={formData.sellInDate}
                onChange={(e) => handleFormChange("sellInDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {(formData.status === "SELL_OUT" || formData.status === "ACTIVATED") && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Sell Out Date"
                  value={formData.sellOutDate}
                  onChange={(e) => handleFormChange("sellOutDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {formData.status === "ACTIVATED" && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Activation Date"
                  value={formData.activationDate}
                  onChange={(e) =>
                    handleFormChange("activationDate", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEdit ? "Save Changes" : "Add Inventory"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => {
                const newStatusValue = e.target.value;
                setNewStatus(newStatusValue);

                let dateToShow = "";
                if (newStatusValue === "SELL_IN" && selectedInventory?.sellInDate) {
                  dateToShow = new Date(selectedInventory.sellInDate).toISOString().slice(0, 10);
                } else if (newStatusValue === "SELL_OUT" && selectedInventory?.sellOutDate) {
                  dateToShow = new Date(selectedInventory.sellOutDate).toISOString().slice(0, 10);
                } else if (newStatusValue === "ACTIVATED" && selectedInventory?.activationDate) {
                  dateToShow = new Date(selectedInventory.activationDate).toISOString().slice(0, 10);
                } else {
                  dateToShow = new Date().toISOString().slice(0, 10);
                }
                setStatusDate(dateToShow);
              }}
            >
              <MenuItem value="SELL_IN">Sell In</MenuItem>
              <MenuItem value="SELL_OUT">Sell Out</MenuItem>
              <MenuItem value="ACTIVATED">Activated</MenuItem>
            </Select>
          </FormControl>

          {newStatus === "SELL_IN" && (
            <TextField
              fullWidth
              type="date"
              label="Sell In Date"
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}

          {newStatus === "SELL_OUT" && (
            <TextField
              fullWidth
              type="date"
              label="Sell Out Date"
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}

          {newStatus === "ACTIVATED" && (
            <TextField
              fullWidth
              type="date"
              label="Activation Date"
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Results Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Upload Results</DialogTitle>
        <DialogContent>
          {uploadResults && (
            <div>
              <div className="mb-4 p-4 bg-gray-50 rounded">
                <div className="text-lg font-semibold mb-2">Summary</div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{uploadResults.total}</div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{uploadResults.success}</div>
                    <div className="text-sm text-gray-600">Success</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{uploadResults.failed}</div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>
              </div>

              {uploadResults.errors.length > 0 && (
                <div>
                  <div className="text-lg font-semibold mb-2 text-red-600">Errors</div>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Row</th>
                          <th className="px-4 py-2 text-left">IMEI</th>
                          <th className="px-4 py-2 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {uploadResults.errors.map((error, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{error.row}</td>
                            <td className="px-4 py-2">{error.imei}</td>
                            <td className="px-4 py-2 text-red-600">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}